import React, { useEffect, useState } from 'react';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';
import supabase from '../../supabase/supabase';
import './BankAccountsManager.css';

const { FiCreditCard, FiPlus, FiSave, FiCheck } = FiIcons;
const emptyAccount = { bank_name: '', account_name: '', account_number: '', branch: '', logo_url: '', active: true, sort_order: 0 };

export default function BankAccountsManager() {
  const [accounts, setAccounts] = useState([]);
  const [draft, setDraft] = useState(emptyAccount);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');

  const load = async () => {
    const { data, error } = await supabase.from('bank_transfer_accounts_1750000019').select('*').order('sort_order').order('created_at');
    if (error) setMessage(`Could not load bank accounts: ${error.message}`);
    else setAccounts(data || []);
  };

  useEffect(() => { load(); }, []);

  const save = async (account) => {
    if (!account.bank_name.trim() || !account.account_name.trim() || !account.account_number.trim()) {
      setMessage('Bank name, account name and account number are required.');
      return;
    }
    if (account.logo_url && !/^https:\/\//i.test(account.logo_url)) {
      setMessage('Logo URL must start with https://');
      return;
    }
    setSaving(true);
    setMessage('');
    const { id, created_at, updated_at, ...values } = account;
    const query = id
      ? supabase.from('bank_transfer_accounts_1750000019').update({ ...values, updated_at: new Date().toISOString() }).eq('id', id)
      : supabase.from('bank_transfer_accounts_1750000019').insert(values);
    const { error } = await query;
    setSaving(false);
    if (error) setMessage(`Could not save bank account: ${error.message}`);
    else {
      setMessage('Bank account saved.');
      if (!id) setDraft(emptyAccount);
      load();
    }
  };

  const fields = [
    ['bank_name', 'Bank Name', 'e.g. Commercial Bank'],
    ['account_name', 'Account Name', 'Transfer recipient'],
    ['account_number', 'Account Number', 'Account number'],
    ['branch', 'Branch', 'Branch name'],
    ['logo_url', 'Bank Logo URL', 'https://example.com/logo.png']
  ];

  const renderFields = (account, update) => (
    <div className="bank-admin-fields">
      {fields.map(([key, label, placeholder]) => (
        <div className="editor-field" key={key}>
          <label>{label}</label>
          <input value={account[key] || ''} onChange={(event) => update({ ...account, [key]: event.target.value })} placeholder={placeholder} />
        </div>
      ))}
      <div className="editor-field">
        <label>Display Order</label>
        <input type="number" value={account.sort_order ?? 0} onChange={(event) => update({ ...account, sort_order: Number(event.target.value) })} />
      </div>
      <label className="bank-admin-active">
        <input type="checkbox" checked={account.active} onChange={(event) => update({ ...account, active: event.target.checked })} />
        Available at checkout
      </label>
    </div>
  );

  return (
    <div className="admin-card bank-admin-card">
      <div className="settings-card-header">
        <div className="settings-icon" style={{ color: '#0e8f5b', background: '#eaf7f1' }}><SafeIcon icon={FiCreditCard} /></div>
        <div><h2>Additional Bank Transfer Options</h2><p className="settings-subtitle">Add bank accounts and logo URLs customers can choose at checkout</p></div>
      </div>
      {message && <p className="bank-admin-message" role="status">{message}</p>}
      {accounts.map((account) => (
        <BankAccountEditor key={account.id} account={account} fields={renderFields} save={save} saving={saving} />
      ))}
      <div className="bank-admin-new">
        <h3>Add Bank Account</h3>
        {renderFields(draft, setDraft)}
        {draft.logo_url && <img className="bank-admin-logo" src={draft.logo_url} alt="New bank logo preview" />}
        <button className="admin-btn primary" onClick={() => save(draft)} disabled={saving}>
          <SafeIcon icon={FiPlus} /> Add Bank Account
        </button>
      </div>
    </div>
  );
}

function BankAccountEditor({ account, fields, save, saving }) {
  const [value, setValue] = useState(account);
  useEffect(() => setValue(account), [account]);

  return (
    <div className="bank-admin-entry">
      <div className="bank-admin-entry-title">
        {value.logo_url && <img className="bank-admin-logo" src={value.logo_url} alt="" />}
        <strong>{value.bank_name || 'Unnamed bank'}</strong>
        <span>{value.active ? 'Visible at checkout' : 'Hidden'}</span>
      </div>
      {fields(value, setValue)}
      <button className="admin-btn primary" onClick={() => save(value)} disabled={saving}>
        <SafeIcon icon={FiSave} /> Save Changes
      </button>
    </div>
  );
}